import React, { memo, useEffect, useRef, useState, useCallback } from 'react';
import { Modal } from 'react-bootstrap';
import { isMobile } from '../utils/data';

type Media = {
  url: string;
  alt?: string;
};

type Props = {
  media: Media[];
  show: boolean;
  onHide: () => void;
  startImageIndex?: number;
  onRemoveImage?: (index: number) => void;
};

const NewImageViewer: React.FC<Props> = ({ show, onHide, media, startImageIndex = 0, onRemoveImage }) => {
  const [currentIndex, setCurrentIndex] = useState(startImageIndex);
  const [imageLoaded, setImageLoaded] = useState<{ [key: number]: boolean }>({});
  const [zoomLevel, setZoomLevel] = useState(1);
  const [panX, setPanX] = useState(0);
  const [panY, setPanY] = useState(0);
  const [isDragging, setIsDragging] = useState(false);
  const touchStartX = useRef<number>(0);
  const touchStartY = useRef<number>(0);
  const lastTouchDistance = useRef<number>(0);
  const dragStartX = useRef<number>(0);
  const dragStartY = useRef<number>(0);
  const containerRef = useRef<HTMLDivElement>(null);
  const imageRef = useRef<HTMLImageElement>(null);

  // Update current index when startImageIndex changes
  useEffect(() => {
    setCurrentIndex(Math.min(startImageIndex, media.length - 1));
  }, [startImageIndex, media.length]);

  // Reset zoom when changing images
  useEffect(() => {
    setZoomLevel(1);
    setPanX(0);
    setPanY(0);
    setIsDragging(false);
  }, [currentIndex]);

  const resetZoom = useCallback(() => {
    setZoomLevel(1);
    setPanX(0);
    setPanY(0);
  }, []);

  const zoomIn = useCallback(() => {
    setZoomLevel(prev => Math.min(prev * 1.5, 5));
  }, []);

  const zoomOut = useCallback(() => {
    setZoomLevel(prev => {
      const newZoom = prev / 1.5;
      if (newZoom <= 1) {
        setPanX(0);
        setPanY(0);
        return 1;
      }
      return newZoom;
    });
  }, []);

  // Calculate touch distance for pinch zoom
  const getTouchDistance = useCallback((touches: React.TouchList) => {
    if (touches.length < 2) return 0;
    const touch1 = touches[0];
    const touch2 = touches[1];
    return Math.sqrt(
      Math.pow(touch2.clientX - touch1.clientX, 2) + 
      Math.pow(touch2.clientY - touch1.clientY, 2)
    );
  }, []);

  const goNext = useCallback(() => {
    if (media.length > 1) {
      setCurrentIndex((prev) => (prev + 1) % media.length);
    }
  }, [media.length]);

  const goPrev = useCallback(() => {
    if (media.length > 1) {
      setCurrentIndex((prev) => (prev - 1 + media.length) % media.length);
    }
  }, [media.length]);

  // Keyboard navigation
  const handleKeyDown = useCallback((e: KeyboardEvent) => {
    if (!show) return;
    
    switch (e.key) {
      case 'ArrowRight':
        if (zoomLevel === 1) goNext();
        break;
      case 'ArrowLeft':
        if (zoomLevel === 1) goPrev();
        break;
      case 'Escape':
        if (zoomLevel > 1) {
          resetZoom();
        } else {
          onHide();
        }
        break;
      case '+':
      case '=':
        zoomIn();
        break;
      case '-':
        zoomOut();
        break;
    }
  }, [show, goNext, goPrev, onHide, zoomLevel, resetZoom, zoomIn, zoomOut]);

  useEffect(() => {
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [handleKeyDown]);

  // Touch gestures for mobile
  const handleTouchStart = useCallback((e: React.TouchEvent) => {
    e.preventDefault();
    const touches = e.touches;
    
    if (touches.length === 1) {
      // Single touch - prepare for pan or swipe
      const touch = touches[0];
      touchStartX.current = touch.clientX;
      touchStartY.current = touch.clientY;
      dragStartX.current = panX;
      dragStartY.current = panY;
      setIsDragging(zoomLevel > 1);
    } else if (touches.length === 2) {
      // Two fingers - prepare for zoom
      lastTouchDistance.current = getTouchDistance(touches);
      setIsDragging(false);
    }
  }, [panX, panY, zoomLevel, getTouchDistance]);

  const handleTouchMove = useCallback((e: React.TouchEvent) => {
    e.preventDefault();
    const touches = e.touches;
    
    if (touches.length === 1 && isDragging && zoomLevel > 1) {
      // Pan when zoomed
      const touch = touches[0];
      const deltaX = touch.clientX - touchStartX.current;
      const deltaY = touch.clientY - touchStartY.current;
      
      setPanX(dragStartX.current + deltaX);
      setPanY(dragStartY.current + deltaY);
    } else if (touches.length === 2) {
      // Pinch zoom
      const currentDistance = getTouchDistance(touches);
      if (lastTouchDistance.current > 0) {
        const scaleFactor = currentDistance / lastTouchDistance.current;
        const newZoom = Math.min(Math.max(zoomLevel * scaleFactor, 1), 5);
        
        if (newZoom === 1) {
          setPanX(0);
          setPanY(0);
        }
        
        setZoomLevel(newZoom);
      }
      lastTouchDistance.current = currentDistance;
    }
  }, [isDragging, zoomLevel, getTouchDistance]);

  const handleTouchEnd = useCallback((e: React.TouchEvent) => {
    const touches = e.changedTouches;
    
    if (touches.length === 1 && !isDragging && zoomLevel === 1) {
      // Handle swipe only when not zoomed and not dragging
      const touch = touches[0];
      const deltaX = touch.clientX - touchStartX.current;
      const deltaY = touch.clientY - touchStartY.current;

      // Only handle horizontal swipes (ignore vertical scrolling)
      if (Math.abs(deltaX) > Math.abs(deltaY) && Math.abs(deltaX) > 50) {
        if (deltaX > 0) {
          goPrev();
        } else {
          goNext();
        }
      }
    }
    
    setIsDragging(false);
    lastTouchDistance.current = 0;
  }, [goNext, goPrev, isDragging, zoomLevel]);

  // Mouse wheel zoom for desktop
  const handleWheel = useCallback((e: React.WheelEvent) => {
    e.preventDefault();
    if (e.deltaY < 0) {
      zoomIn();
    } else {
      zoomOut();
    }
  }, [zoomIn, zoomOut]);

  // Mouse drag for desktop
  const handleMouseDown = useCallback((e: React.MouseEvent) => {
    if (zoomLevel > 1) {
      e.preventDefault();
      setIsDragging(true);
      dragStartX.current = panX;
      dragStartY.current = panY;
      touchStartX.current = e.clientX;
      touchStartY.current = e.clientY;
    }
  }, [zoomLevel, panX, panY]);

  const handleMouseMove = useCallback((e: React.MouseEvent) => {
    if (isDragging && zoomLevel > 1) {
      e.preventDefault();
      const deltaX = e.clientX - touchStartX.current;
      const deltaY = e.clientY - touchStartY.current;
      
      setPanX(dragStartX.current + deltaX);
      setPanY(dragStartY.current + deltaY);
    }
  }, [isDragging, zoomLevel]);

  const handleMouseUp = useCallback(() => {
    setIsDragging(false);
  }, []);

  // Add mouse event listeners
  useEffect(() => {
    if (isDragging) {
      const handleGlobalMouseMove = (e: MouseEvent) => {
        if (isDragging && zoomLevel > 1) {
          e.preventDefault();
          const deltaX = e.clientX - touchStartX.current;
          const deltaY = e.clientY - touchStartY.current;
          
          setPanX(dragStartX.current + deltaX);
          setPanY(dragStartY.current + deltaY);
        }
      };

      const handleGlobalMouseUp = () => {
        setIsDragging(false);
      };

      document.addEventListener('mousemove', handleGlobalMouseMove);
      document.addEventListener('mouseup', handleGlobalMouseUp);

      return () => {
        document.removeEventListener('mousemove', handleGlobalMouseMove);
        document.removeEventListener('mouseup', handleGlobalMouseUp);
      };
    }
  }, [isDragging, zoomLevel]);

  const handleImageLoad = useCallback((index: number) => {
    setImageLoaded(prev => ({ ...prev, [index]: true }));
  }, []);

  const handleThumbnailClick = useCallback((index: number) => {
    setCurrentIndex(index);
  }, []);

  useEffect(() => {
    if (!show) return;
    const preventGesture = (e: Event) => e.preventDefault();
    document.documentElement.classList.add('no-page-zoom');
    document.addEventListener('gesturestart', preventGesture);
    document.addEventListener('gesturechange', preventGesture);
    document.addEventListener('gestureend', preventGesture);
    return () => {
      document.documentElement.classList.remove('no-page-zoom');
      document.removeEventListener('gesturestart', preventGesture);
      document.removeEventListener('gesturechange', preventGesture);
      document.removeEventListener('gestureend', preventGesture);
    };
  }, [show]);

  // Reset zoom when modal closes
  useEffect(() => {
    if (!show) {
      resetZoom();
      setIsDragging(false);
    }
  }, [show, resetZoom]);

  if (!show || !media.length) return null;

  return (
    <Modal
      show={show}
      onHide={onHide}
      centered={!isMobile()}
      fullscreen
      contentClassName="p-0 bg-dark border-0"
      dialogClassName="photoswipe-viewer-modal"
    >
      <style>{`
        .photoswipe-viewer-modal { overflow-x: hidden; }
        :root.no-page-zoom { touch-action: none; overscroll-behavior: none; }
        .photoswipe-viewer-modal .modal-dialog {
          width: 100%; /* avoid 100vw to prevent horizontal scrollbar */
          max-width: 100%;
          margin: 0;
          height: 100vh;
        }
        .photoswipe-viewer-modal .modal-content {
          background: #000;
            border: none;
            border-radius: 0;
            height: 100vh;
            width: 100%; /* changed from 100vw */
            box-shadow: none;
            overflow: hidden;
        }
        .photoswipe-viewer-modal .modal-body { /* unchanged layout container */
          padding: 0;
          height: 100%;
          width: 100%;
          display: flex;
          overflow: hidden;
        }
        .viewer-container { /* ensure no horizontal scroll inside */
          position: relative;
          width: 100%;
          height: 100vh;
          display: flex;
          flex-direction: column;
          background: #000;
          overflow: hidden;
        }
        .viewer-header {
          position: absolute;
          top: 0;
          left: 0;
          right: 0;
          z-index: 20;
          display: flex;
          justify-content: space-between;
          align-items: center;
          padding: 12px 16px;
          background: linear-gradient(180deg, rgba(0,0,0,0.6) 0%, rgba(0,0,0,0) 100%);
        }
        .viewer-actions {
          display: flex;
          gap: 12px;
        }
        .viewer-btn {
          width: 44px;
          height: 44px;
          border-radius: 50%;
          background: rgba(0,0,0,0.5);
          color: white;
          border: none;
          display: flex;
          align-items: center;
          justify-content: center;
          cursor: pointer;
          transition: all 0.2s ease;
          backdrop-filter: blur(4px);
        }
        .viewer-btn:hover {
          background: rgba(255,75,110,0.8);
          transform: scale(1.05);
        }
        .viewer-btn:disabled {
          opacity: 0.5;
          cursor: not-allowed;
          background: rgba(0,0,0,0.3);
        }
        .viewer-btn:disabled:hover {
          background: rgba(0,0,0,0.3);
          transform: none;
        }
        .image-container {
          flex: 0.8;
          position: relative;
          display: flex;
          align-items: center;
          justify-content: center;
          overflow: hidden;
          touch-action: manipulation;
          user-select: none;
        }
        .main-image {
          max-width: 100%;
          max-height: 100%;
          width: auto;
          height: auto;
          object-fit: contain;
          user-select: none;
          transition: opacity 0.3s ease;
        }
        .image-loading {
          opacity: 0.7;
        }
        .nav-arrow {
          position: absolute;
          top: 50%;
          transform: translateY(-50%);
          z-index: 15;
          width: 48px;
          height: 48px;
          border-radius: 12px;
          background: rgba(0, 0, 0, 0.7);
          color: white;
          border: 2px solid rgba(255, 255, 255, 0.2);
          display: flex;
          align-items: center;
          justify-content: center;
          cursor: pointer;
          transition: all 0.3s ease;
          box-shadow: 0 4px 20px rgba(0, 0, 0, 0.4);
          backdrop-filter: blur(10px);
          font-size: 20px;
        }
        .nav-arrow:hover {
          background: rgba(255, 75, 110, 0.9);
          border-color: rgba(255, 75, 110, 0.8);
          color: white;
          transform: translateY(-50%) scale(1.1);
          box-shadow: 0 6px 25px rgba(255, 75, 110, 0.4);
        }
        .nav-arrow:active {
          transform: translateY(-50%) scale(0.95);
        }
        .nav-arrow:focus {
          outline: none;
          box-shadow: 0 6px 25px rgba(255, 75, 110, 0.4), 0 0 0 3px rgba(255, 75, 110, 0.3);
        }
        .nav-arrow.prev {
          left: 20px;
        }
        .nav-arrow.next {
          right: 20px;
        }
        .viewer-footer {
          bottom: 0;
          left: 0;
          right: 0;
          z-index: 20;
          padding: 16px;
        }
        .thumbnails {
          display: ${media.length > 1 ? 'flex' : 'none'};
          gap: 12px;
          justify-content: center;
          align-items: center;
          padding: 0 16px;
        }
        .dot {
          width: 12px;
          height: 12px;
          border-radius: 50%;
          background: rgba(255, 255, 255, 0.4);
          cursor: pointer;
          transition: all 0.3s ease;
          border: 2px solid transparent;
        }
        .dot.active {
          background: #FF4B6E;
          transform: scale(1.2);
          box-shadow: 0 0 8px rgba(255, 75, 110, 0.6);
        }
        .dot:hover {
          background: rgba(255, 255, 255, 0.8);
          transform: scale(1.1);
        }
        .dot.active:hover {
          background: #FF4B6E;
          transform: scale(1.2);
        }
        .loading-spinner {
          position: absolute;
          top: 50%;
          left: 50%;
          transform: translate(-50%, -50%);
          color: white;
          font-size: 24px;
        }
        @media (max-width: 768px) {
          .photoswipe-viewer-modal .modal-dialog { width: 100%; max-width: 100%; }
          .nav-arrow {
            width: 42px;
            height: 42px;
            border-radius: 10px;
            font-size: 18px;
          }
          .nav-arrow.prev {
            left: 16px;
          }
          .nav-arrow.next {
            right: 16px;
          }
          .viewer-header {
            padding: 8px 12px;
          }
          .thumbnails {
            padding: 0 12px;
          }
        }
      `}</style>
      
      <Modal.Body className="p-0">
        <div 
          className="viewer-container"
          ref={containerRef}
          onTouchStart={handleTouchStart}
          onTouchMove={handleTouchMove}
          onTouchEnd={handleTouchEnd}
        >
          {/* Header with controls */}
          <div className="viewer-header">
            <div className="viewer-actions">
              {!isMobile() && (
                <>
                  <button 
                    className="viewer-btn"
                    onClick={zoomOut}
                    disabled={zoomLevel <= 1}
                    title="Zoom out"
                  >
                    <i className="feather-minus font-sm" />
                  </button>
                  <button 
                    className="viewer-btn"
                    onClick={zoomIn}
                    disabled={zoomLevel >= 5}
                    title="Zoom in"
                  >
                    <i className="feather-plus font-sm" />
                  </button>
                </>
              )}
              {onRemoveImage && (
                <button 
                  className="viewer-btn"
                  onClick={() => onRemoveImage(currentIndex)}
                  title="Delete image"
                >
                  <i className="feather-trash-2 font-sm" />
                </button>
              )}
            </div>
            <button 
              className="viewer-btn"
              onClick={onHide}
              title="Close"
            >
              <i className="feather-x font-md" />
            </button>
          </div>

          {/* Main image area */}
          <div 
            className="image-container"
            onWheel={handleWheel}
            onMouseDown={handleMouseDown}
            onMouseMove={handleMouseMove}
            onMouseUp={handleMouseUp}
          >
            {media.length > 1 && !isMobile() && (
              <>
                <button 
                  className="nav-arrow prev"
                  onClick={goPrev}
                  aria-label="Previous image"
                >
                  <i className="feather-chevron-left font-lg" />
                </button>
                <button 
                  className="nav-arrow next"
                  onClick={goNext}
                  aria-label="Next image"
                >
                  <i className="feather-chevron-right font-lg" />
                </button>
              </>
            )}
            
            {!imageLoaded[currentIndex] && (
              <div className="loading-spinner">
                <i className="feather-loader animate-spin" />
              </div>
            )}
            
            <img
              ref={imageRef}
              src={media[currentIndex]?.url}
              alt={media[currentIndex]?.alt || `Image ${currentIndex + 1}`}
              className={`main-image ${!imageLoaded[currentIndex] ? 'image-loading' : ''}`}
              onLoad={() => handleImageLoad(currentIndex)}
              draggable={false}
              style={{
                transform: `scale(${zoomLevel}) translate(${panX / zoomLevel}px, ${panY / zoomLevel}px)`,
                cursor: zoomLevel > 1 ? (isDragging ? 'grabbing' : 'grab') : 'default'
              }}
            />
          </div>

          {/* Footer with counter and thumbnails */}
          <div className="viewer-footer">
            {media.length > 1 && (
              <div className="thumbnails">
                {media.map((_, index) => (
                  <div
                    key={index}
                    className={`dot ${index === currentIndex ? 'active' : ''}`}
                    onClick={() => handleThumbnailClick(index)}
                    aria-label={`Go to image ${index + 1}`}
                    title={`Image ${index + 1}`}
                  />
                ))}
              </div>
            )}
          </div>
        </div>
      </Modal.Body>
    </Modal>
  );
};

export default memo(NewImageViewer);
